import { IonButton, IonIcon } from "@ionic/react";
import {
  volumeHigh,
  volumeLow,
  volumeMedium,
  volumeMute,
} from "ionicons/icons";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePlayer } from "../data/store/usePlayer";

const iconFor = (effective: number) => {
  if (effective <= 0) return volumeMute;
  if (effective < 0.34) return volumeLow;
  if (effective < 0.67) return volumeMedium;
  return volumeHigh;
};

const PANEL_WIDTH = 260;
const VIEWPORT_MARGIN = 8;

type Anchor = { top: number; left: number };

const VolumeControl: React.FC = () => {
  const volume = usePlayer((s) => s.volume);
  const isMuted = usePlayer((s) => s.isMuted);
  const setVolume = usePlayer((s) => s.setVolume);
  const toggleMute = usePlayer((s) => s.toggleMute);
  const setVolumeOpen = usePlayer((s) => s.setVolumeOpen);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const buttonRef = useRef<HTMLIonButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const effective = isMuted ? 0 : volume;
  const icon = iconFor(effective);
  const percent = Math.round(effective * 100);

  const positionAnchor = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    // Center the panel above the button, then clamp inside the viewport.
    let left = rect.left + rect.width / 2 - PANEL_WIDTH / 2;
    if (left + PANEL_WIDTH > vw - VIEWPORT_MARGIN) {
      left = vw - VIEWPORT_MARGIN - PANEL_WIDTH;
    }
    if (left < VIEWPORT_MARGIN) {
      left = VIEWPORT_MARGIN;
    }
    setAnchor({ top: rect.top - 8, left });
  };

  const handleToggle = () => {
    if (!open) positionAnchor();
    setOpen((x) => !x);
  };

  // Close when clicking outside the button or the panel.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (buttonRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Broadcast popover state to the store so the side menu can suppress its
  // swipe-to-open gesture while the user is dragging the volume slider.
  useEffect(() => {
    setVolumeOpen(open);
    return () => setVolumeOpen(false);
  }, [open, setVolumeOpen]);

  // Stop pointer/touch events from bubbling past the panel so Ionic's
  // document-level swipe-to-open gesture detector never sees them. This is
  // belt-and-suspenders on top of disabling the menu's swipeGesture prop.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const stop = (e: Event) => e.stopPropagation();
    const events = [
      "pointerdown", "pointermove",
      "touchstart", "touchmove",
      "mousedown", "mousemove",
    ];
    events.forEach((ev) => panel.addEventListener(ev, stop));
    return () => {
      events.forEach((ev) => panel.removeEventListener(ev, stop));
    };
  }, [open]);

  // Close on Escape, reposition on resize/scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onReflow = () => positionAnchor();
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open]);

  return (
    <>
      <IonButton
        ref={buttonRef}
        onClick={handleToggle}
        aria-label={`Volume ${percent}%${isMuted ? " (muted)" : ""}`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <IonIcon icon={icon} aria-hidden="true" />
      </IonButton>
      {open && anchor && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Volume control"
          style={{
            position: "fixed",
            top: anchor.top,
            left: anchor.left,
            transform: "translateY(-100%)",
            background: "var(--ion-background-color, #fff)",
            color: "var(--ion-text-color, #000)",
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 10,
            padding: "6px 10px",
            display: "flex",
            alignItems: "center",
            gap: 6,
            width: PANEL_WIDTH,
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            zIndex: 20000,
          }}
        >
          <IonButton
            fill="clear"
            size="small"
            onClick={toggleMute}
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            <IonIcon icon={icon} aria-hidden="true" />
          </IonButton>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={effective}
            onChange={(e) => setVolume(Number(e.target.value))}
            aria-label="Volume"
            className="volume-slider"
            style={{ flex: 1, cursor: "pointer" }}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default VolumeControl;
